<template>
  <div>
    <div class="required-notice">빨간별표 표시는 필수 입력사항입니다.</div>

    <!-- 저장 확인 팝업 -->
    <vs-popup title="저장" :active.sync="showSaveConfirm">
      <p class="save-confirm-text">
        <span class="save-confirm-highlight">[{{ form.점검연도 }}년, {{ form.점검구분 }}]</span>
        수시점검 사항을 저장하시겠습니까?
      </p>
      <div class="flex justify-end gap-3 mt-6">
        <vs-button type="flat" color="grey" @click="onCancelSave">취소</vs-button>
        <vs-button color="secondary" @click="onConfirmSave">저장</vs-button>
      </div>
    </vs-popup>

    <div class="mt-28px">
      <h5 ref="refYeong" class="required"><i class="dot"></i> 점검연도 <number-input v-model="form.점검연도" :useComma="false" :align="'left'" style="width: 114px; display: inline-block" class="ml-10 mr-2" /><span>년</span></h5>

      <div class="writeGridContainer mt-28px">
        <div>
          <div>
            <h5 class="required"><i class="dot"></i> 점검구분</h5>
          </div>
          <div ref="refGubun" class="flex gap-3">
            <vs-radio v-for="ri in ['해빙기', '우수기', '동절기', '기타']" :key="`점검구분-${ri}`" v-model="form.점검구분" :disabled="board.readonly" :vs-value="ri">{{ ri }}</vs-radio>
          </div>
        </div>
      </div>
    </div>

    <!--    기본정보-->
    <field-select-module v-model="form.stdInfo" :types="fieldTypes" :board="this.board" :is-print="isPrint" class="mt-28px" :useFieldInfo="false" />

    <div>
      <vs-row class="mt-6">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5><i class="dot"></i> 점검개요</h5>
        </vs-col>
      </vs-row>

      <div class="writeGridContainer mt-4">
        <div ref="refGigan">
          <div class="required">점검기간</div>
          <date-selector-rang :is-print="isPrint" class="w-full" v-model="form.점검기간"></date-selector-rang>
        </div>
        <div ref="refSiseol" class="writeGridLineItem">
          <div class="required">점검대상 시설명</div>
          <p v-if="isPrint" class="print-input">{{ fieldNames }}</p>
          <field-selector v-else v-model="form.점검대상시설명" multiple />
        </div>
        <div ref="refSogwan" class="writeGridLineItem">
          <div class="required">소관부서</div>
          <span class="print-input">{{ 소관부서 }}</span>
        </div>
        <div ref="refBan" class="writeGridLineItem">
          <div class="required">점검반</div>
          <p v-if="isPrint" class="print-input">{{ form.점검반 }}</p>
          <ml-input v-else v-model="form.점검반" class="w-full" />
        </div>
      </div>
    </div>

    <table-custom
      ref="refTable"
      :board="board"
      :is-print="isPrint"
      :title="'<span style=\'color: #ff0000;font-weight: 600;margin-right: 4px;\'>*</span>점검 결과 및 조치사항'"
      :title-type="'html'"
      class="mt-4"
      v-model="form.조치사항"
      :tableData="[
        { id: '구분', title: '구분', type: 'text', placeholder: '00사면' },
        { id: '점검결과', title: '점검 결과', type: 'textarea', placeholder: '⁃ 사면내 기울어진 수목 자생\n⁃ 절토사면내 누수 발생' },
        { id: '조치사항', title: '조치(해야 할) 사항', type: 'textarea', placeholder: '⁃ 수목 제거 필요\n⁃ 민간전문가 합동점검 필요' },
      ]"
      :mobile="['구분']"
    />

    <div>
      <vs-row class="mt-6">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5 class="flex items-center gap-2">
            <i class="dot"></i> 조치 계획
            <vs-checkbox v-model="form.조치계획_해당사항없음" :disabled="board.readonly" class="ml-2">해당사항 없음</vs-checkbox>
          </h5>
        </vs-col>
      </vs-row>

      <div ref="refJochi" class="mt-4" v-if="!form.조치계획_해당사항없음">
        <p v-if="isPrint" class="print-textarea">{{ form.조치계획 }}</p>
        <ml-textarea
          v-else
          v-model="form.조치계획"
          class="w-full"
          placeholder="❍ 점검결과에 따른 조치사항 소관부서에 통보
❍ 시설물 점검결과에 따른 조치 사항 이행여부 확인 및 점검"
        />
      </div>
    </div>

    <!--    사진대지-->
    <PhotoUploadGridModule ref="refPhoto" :row-count="2" :grid-count="2" v-model="form.photoList" :isPrint="isPrint" :readonly="board.readonly" section-title="점검 사진" placeholder="신축현장 점검" :showFooterInput="false" showCustom :customTitle="true">
      <template #customTitle>
        <h5 class="flex items-center required"><i class="dot"></i> 점검 사진</h5>
      </template>
      <template v-slot:custom="{ index }">
        <div>
          <div class="w-full flex gap-3 items-center mt-2">
            <span class="whitespace-no-wrap required" style="font-weight: 500">날짜</span>
            <span v-if="isPrint" class="print-input">{{ form.photoList[index].date }}</span>
            <date-selector v-else v-model="form.photoList[index].date" :readonly="board.readonly" class="flex-1" />
            <span class="whitespace-no-wrap required" style="font-weight: 500">장소</span>
            <span v-if="isPrint" class="print-input">{{ form.photoList[index].place }}</span>
            <ml-input v-else v-model="form.photoList[index].place" :readonly="board.readonly" class="w-full flex-1" />
          </div>
        </div>
        <div class="w-full flex gap-3 items-center mt-2">
          <span class="whitespace-no-wrap required" style="font-weight: 500">내용</span>
          <span v-if="isPrint" class="print-input">{{ form.photoList[index].input }}</span>
          <ml-input v-else v-model="form.photoList[index].input" :readonly="board.readonly" class="w-full flex-1" />
        </div>
      </template>
    </PhotoUploadGridModule>
    <!--    파일첨부-->
    <file-upload-module ref="refFile" id="fileList" class="w-full mt-4" title="파일첨부" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly">
      <template #titleRight>
        <span style="color: #ff0000; font-weight: 600; margin-left: 2px;">*</span>
      </template>
    </file-upload-module>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule";
import DateSelectorRang from "@/components/selector/DateSelectorRang.vue";
import TableCustom from "@/components/TableCustom.vue";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import NumberInput from "@/components/NumberInput.vue";

export default {
  components: {
    FieldSelector,
    DateSelector,
    PhotoUploadGridModule,
    TableCustom,
    DateSelectorRang,
    FileUploadModule,
    FieldSelectModule,
    NumberInput,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  computed: {
    fieldTypes() {
      return ["com_name", "rank", "writer_name", "date"];
    },
    fieldNames() {
      const fields = this.form.점검대상시설명.map((item) => {
        return item.field_name;
      });
      return fields.join(", ");
    },
    projectId() {
      return this.$route.params.proj_id;
    },
    소관부서() {
      if (this.form.점검대상시설명 && this.form.점검대상시설명.length > 0) {
        const 소관부서 = this.form.점검대상시설명.map((item) => {
          const meta = item.meta ? JSON.parse(item.meta) : {};
          return meta.소관부서;
        });
        return [...new Set(소관부서)].filter(Boolean).join(", ");
      }
      return "";
    },
    showWorkplaceInfo() {
      // 사업장정보 안보이게 함
      return parseInt(this.projectId) !== 5;
    },
  },
  data() {
    return {
      allCheck: false,
      showSaveConfirm: false,
      _saveResolve: null,
      form: {
        점검연도: new Date().getFullYear(),
        점검구분: "",

        stdInfo: {},
        photoList: [],
        fileList: [],

        점검기간: {},
        점검대상시설명: [],
        점검반: "",

        조치사항: [
          {
            id: 1,
            구분: "",
            점검결과: "",
            조치사항: "",
          },
          {
            id: 2,
            구분: "",
            점검결과: "",
            조치사항: "",
          },
        ],
        조치계획: "",
        조치계획_해당사항없음: false,
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key] !== undefined) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    validate(options = {}) {
      if (options && options.isDraft) {
        return true;
      }

      // ref로 지정된 요소로 스크롤 + 내부 focusable 요소 포커스
      const getEl = (refName) => {
        const ref = this.$refs[refName];
        if (!ref) return null;
        return ref.$el || ref;
      };

      const scrollTo = (refName) => () => {
        const el = getEl(refName);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(function() {
          const focusable = el.querySelector('input:not([type="hidden"]), textarea');
          if (focusable) focusable.focus();
        }, 400);
      };

      // 스크롤 후 내부 첫 번째 클릭 가능 요소 클릭 (드롭다운 오픈용)
      const scrollAndClick = (refName) => () => {
        const el = getEl(refName);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(function() {
          const clickable = el.querySelector('input:not([type="hidden"]), [role="combobox"], button, .vs-select--input');
          if (clickable) clickable.click();
          else el.click();
        }, 400);
      };

      const messages = [];

      if (!this.form.점검연도) {
        messages.push({ text: '[연도]를 입력하여 주십시오.', action: scrollTo('refYeong') });
      }
      if (!this.form.점검구분) {
        messages.push({ text: '[점검구분]의 해당 점검에 체크하여 주십시오.', action: scrollTo('refGubun') });
      }
      if (!this.form.점검기간 || !this.form.점검기간.sDate || !this.form.점검기간.eDate) {
        messages.push({ text: '[점검개요]의 "점검기간"을 입력하여 주십시오.', action: scrollAndClick('refGigan') });
      }
      if (!this.form.점검대상시설명 || this.form.점검대상시설명.length === 0) {
        messages.push({ text: '[점검개요]의 "점검대상 시설"을 선택하여 주십시오.', action: scrollAndClick('refSiseol') });
      }
      if (!this.소관부서) {
        messages.push({ text: '[점검개요]의 "소관부서"를 입력하여 주십시오.', action: scrollAndClick('refSiseol') });
      }
      if (!this.form.점검반 || String(this.form.점검반).trim() === "") {
        messages.push({ text: '[점검개요]의 "점검반"을 입력하여 주십시오.', action: scrollTo('refBan') });
      }

      const 구분있음 =
        Array.isArray(this.form.조치사항) &&
        this.form.조치사항.some(
          (row) => row && row.구분 !== undefined && row.구분 !== null && String(row.구분).trim() !== ""
        );
      if (!구분있음) {
        messages.push({ text: '[점검 결과 및 조치사항]의 "구분"을 입력하여 주십시오.', action: scrollTo('refTable') });
      }

      const 점검결과있음 =
        Array.isArray(this.form.조치사항) &&
        this.form.조치사항.some(
          (row) => row && row.점검결과 !== undefined && row.점검결과 !== null && String(row.점검결과).trim() !== ""
        );
      if (!점검결과있음) {
        messages.push({ text: '[점검 결과 및 조치사항]의 "점검 결과"를 입력하여 주십시오.', action: scrollTo('refTable') });
      }

      if (!this.form.조치계획_해당사항없음 && (!this.form.조치계획 || String(this.form.조치계획).trim() === "")) {
        messages.push({
          text: '[조치 계획]을 입력하여 주십시오.',
          sub: "해당사항이 없을 경우 '해당사항 없음'에 체크하여 주십시오.",
          action: scrollTo('refJochi'),
        });
      }

      if (!this.form.photoList || this.form.photoList.length === 0) {
        messages.push({ text: '[점검 사진]을 첨부하여 주십시오.', action: scrollTo('refPhoto') });
      } else {
        const missingDate = this.form.photoList.some(function(p) { return !p.date; });
        if (missingDate) {
          messages.push({ text: '[점검 사진]의 "날짜"를 입력하여 주십시오.', action: scrollTo('refPhoto') });
        }
        const missingPlace = this.form.photoList.some(function(p) { return !p.place || String(p.place).trim() === ''; });
        if (missingPlace) {
          messages.push({ text: '[점검 사진]의 "장소"를 입력하여 주십시오.', action: scrollTo('refPhoto') });
        }
        const missingContent = this.form.photoList.some(function(p) { return !p.input || String(p.input).trim() === ''; });
        if (missingContent) {
          messages.push({ text: '[점검 사진]의 "내용"을 입력하여 주십시오.', action: scrollTo('refPhoto') });
        }
      }

      if (!this.form.fileList || this.form.fileList.length === 0) {
        messages.push({ text: '[파일첨부]점검 관련 파일을 첨부하여 주십시오.', action: scrollTo('refFile') });
      }

      if (messages.length > 0) {
        window.dispatchEvent(new CustomEvent("requiredFieldAlert", { detail: { messages } }));
        return false;
      }

      return new Promise((resolve) => {
        this._saveResolve = resolve;
        this.showSaveConfirm = true;
      });
    },

    onConfirmSave() {
      this.showSaveConfirm = false;
      if (this._saveResolve) {
        this._saveResolve(true);
        this._saveResolve = null;
      }
    },

    onCancelSave() {
      this.showSaveConfirm = false;
      if (this._saveResolve) {
        this._saveResolve(false);
        this._saveResolve = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$border: 1px solid #cdcdcd;
table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    th {
      text-align: center;
      font-size: 14px;
      font-weight: 500;
      line-height: 17px;

      background: #f9f9f9;
      border: solid #cdcdcd 1px;
      padding: 6px;
    }
  }
  tbody {
    tr {
      td {
        font-size: 14px;
        font-weight: 500;
        line-height: 17px;
        padding: 5px;
        border: solid #cdcdcd 1px;

        div {
          align-items: center;
        }
      }
    }
  }
}

.save-confirm-text {
  font-size: 15px;
  line-height: 1.6;
}

.save-confirm-highlight {
  color: #7e72f2;
  font-weight: 600;
}

.required-notice {
  border-radius: 5px;
  border: 1px solid #cdcdcd;
  padding: 12px 16px;
  font-size: 16px;
  color: #2C2C2C;
  font-weight: 700;

  &::before {
    content: "* ";
    color: #ff0000;
    font-weight: 600;
  }
}

.required::before {
  content: "*";
  color: #ff0000;
  font-weight: 600;
  margin-right: 4px;
}
</style>
